import React, { useEffect, useRef, useState } from 'react';
import { inversionAPI, InvResult } from '../../services/api';

// /inversion — 微地震裂缝反演 (方向②): 俯视图缝网重构 + 逐段指标
const TH = {
  bg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)', text: '#1E2A3A', textSec: '#4F6680',
  textMuted: '#8598AD', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  accent: '#C2533A', frac: '#C2533A', well: '#1E2A3A',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
const STAGE_COLORS = ['#2874A6', '#1E8449', '#B7950B', '#884EA0', '#C0392B', '#148F77',
  '#AF601A', '#2E4053', '#6C3483', '#1F618D', '#A04000', '#117864'];

function PlanView({ res, sel }: { res: InvResult; sel: number | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const stages = Object.entries(res.stages);
    const allPts = stages.flatMap(([, st]) => st.events);
    if (!allPts.length) return;
    const xs = allPts.map((p) => p[0]), ys = allPts.map((p) => p[1]);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const m = 30;
    const sc = Math.min((W - 2 * m) / (x1 - x0 || 1), (H - 2 * m) / (y1 - y0 || 1));
    const px = (x: number) => m + (x - x0) * sc + (W - 2 * m - (x1 - x0) * sc) / 2;
    const py = (y: number) => H - m - (y - y0) * sc - (H - 2 * m - (y1 - y0) * sc) / 2;
    // 事件点
    stages.forEach(([k, st]) => {
      const stg = Number(k);
      const dim = sel !== null && sel !== stg;
      ctx.fillStyle = STAGE_COLORS[stg % STAGE_COLORS.length] + (dim ? '22' : '99');
      st.events.forEach((p) => {
        ctx.beginPath(); ctx.arc(px(p[0]), py(p[1]), dim ? 1.5 : 2.5, 0, 2 * Math.PI); ctx.fill();
      });
    });
    // 缝网轮廓 (Alpha Shape 凹包)。画在事件点之上、井轨迹之下:
    // 这是"这一段到底改造了多大范围"的直观表达, 面积即表中的 SRA 凹包。
    // 用半透明填充而非只描边 —— 相邻段重叠时能看出改造范围的交叠关系。
    stages.forEach(([k, st]) => {
      const poly = st.sra_alpha?.polygon;
      if (!poly || poly.length < 3) return;
      const stg = Number(k);
      const dim = sel !== null && sel !== stg;
      const color = STAGE_COLORS[stg % STAGE_COLORS.length];
      ctx.beginPath();
      poly.forEach((pt, n) => (n ? ctx.lineTo(px(pt[0]), py(pt[1])) : ctx.moveTo(px(pt[0]), py(pt[1]))));
      ctx.closePath();
      ctx.fillStyle = color + (dim ? '0D' : '26');
      ctx.fill();
      ctx.strokeStyle = color + (dim ? '33' : 'AA');
      ctx.lineWidth = dim ? 0.8 : 1.6;
      ctx.stroke();
    });

    // 井轨迹 (俯视投影)
    if (res.well && res.well.length > 1) {
      ctx.strokeStyle = TH.well; ctx.lineWidth = 2.5; ctx.beginPath();
      res.well.forEach((p, i) => { i ? ctx.lineTo(px(p[0]), py(p[1])) : ctx.moveTo(px(p[0]), py(p[1])); });
      ctx.stroke();
    }
    // 缝面走向线 (多边形俯视投影为线段)
    stages.forEach(([k, st]) => {
      const stg = Number(k);
      const dim = sel !== null && sel !== stg;
      ctx.strokeStyle = dim ? 'rgba(194,83,58,0.15)' : TH.frac;
      ctx.lineWidth = dim ? 1 : 2.4;
      st.fractures.forEach((f) => {
        const qx = f.polygon.map((p) => p[0]), qy = f.polygon.map((p) => p[1]);
        // 取沿走向的两个端点
        let a = 0, b = 0, dmax = -1;
        for (let i = 0; i < qx.length; i++) for (let j = i + 1; j < qx.length; j++) {
          const d = (qx[i] - qx[j]) ** 2 + (qy[i] - qy[j]) ** 2;
          if (d > dmax) { dmax = d; a = i; b = j; }
        }
        ctx.beginPath(); ctx.moveTo(px(qx[a]), py(qy[a])); ctx.lineTo(px(qx[b]), py(qy[b])); ctx.stroke();
      });
    });
    ctx.fillStyle = TH.textMuted; ctx.font = `11px ${TH.fontMono}`;
    ctx.fillText(`E → (${(x1 - x0).toFixed(0)} m)`, W - 130, H - 8);
    ctx.save(); ctx.translate(12, 90); ctx.rotate(-Math.PI / 2); ctx.fillText(`N → (${(y1 - y0).toFixed(0)} m)`, 0, 0); ctx.restore();
  });
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

export default function InversionPage() {
  const [res, setRes] = useState<InvResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const run = async (file?: File) => {
    setLoading(true); setErr('');
    try {
      const r = await inversionAPI.run(file);
      setRes(r.data); setSel(null);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || '反演失败');
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const s = res?.summary;
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: TH.text }}>裂缝反演 · 微地震缝网重构</span>
        <span style={{ fontSize: 12, color: TH.textMuted }}>DBSCAN 聚类 + RANSAC 走向拟合(近垂直缝假设) · 缝高由压裂模拟(P3D)正演给出</span>
        <span style={{ flex: 1 }} />
        <button onClick={() => fileRef.current?.click()} disabled={loading}
          style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${TH.headerBorder}`, background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          ↑ 导入微地震事件 Excel
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) run(f); e.currentTarget.value = ''; }} />
        <button onClick={() => run()} disabled={loading}
          style={{ padding: '4px 12px', borderRadius: 4, border: 'none', background: TH.accent, color: '#fff', fontSize: 12, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? '反演中…' : '↻ 重新反演(演示数据)'}
        </button>
      </div>

      {s && (
        <div style={{ padding: '8px 20px', display: 'flex', gap: 24, borderBottom: `1px solid ${TH.headerBorder}`, background: TH.panelHeaderBg, fontSize: 12, color: TH.textSec, flexWrap: 'wrap' }}>
          <span>事件 <b style={{ color: TH.text }}>{s.n_events}</b></span>
          <span>段数 <b style={{ color: TH.text }}>{s.n_stages}</b></span>
          <span>反演缝面 <b style={{ color: TH.accent }}>{s.n_fractures}</b></span>
          <span>平均拟合 RMS <b style={{ color: TH.text }}>{s.mean_fit_rms_m ?? '—'} m</b></span>
          <span>SRA 凸包 <b style={{ color: TH.text }}>{s.total_sra_1e4m2} 万m²</b></span>
          <span title="Alpha Shape 凹包, 不填充缝间空白, 比凸包更接近真实改造面积">
            SRA 凹包 <b style={{ color: TH.accent }}>{s.total_sra_alpha_1e4m2} 万m²</b>
          </span>
          <span title={s.srv_depth_reliable ? '三维 Delaunay Alpha Shape 体积' : '事件深度疑为投影伪深度, 三维体积仅供参考'}>
            SRV <b style={{ color: s.srv_depth_reliable ? TH.text : TH.textMuted }}>{s.total_srv_1e4m3} 万m³</b>
            {!s.srv_depth_reliable && <b style={{ color: '#C0392B', marginLeft: 4 }}>深度存疑</b>}
          </span>
          {res?.source === 'demo' && <span style={{ color: TH.textMuted }}>(演示数据)</span>}
        </div>
      )}
      {err && <div style={{ padding: '10px 20px', color: '#C0392B', fontSize: 13 }}>{err}</div>}

      {res && (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 340px', gap: 1, minHeight: 0, background: TH.headerBorder }}>
          <div style={{ background: TH.bg }}><PlanView res={res} sel={sel} /></div>
          <div style={{ background: TH.bg, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: TH.fontMono }}>
              <thead>
                <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
                  {['段', '事件', '缝数', '内点率', 'RMS m', 'SRA 凸包', 'SRA 凹包', 'SRV'].map((h) => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'right', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(res.stages).map(([k, st]) => {
                  const stg = Number(k);
                  return (
                    <tr key={k} onClick={() => setSel(sel === stg ? null : stg)}
                      style={{ cursor: 'pointer', background: sel === stg ? 'rgba(194,83,58,0.07)' : undefined }}>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: STAGE_COLORS[stg % STAGE_COLORS.length], fontWeight: 700 }}>{k}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.textSec }}>{st.metrics.n_events}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.text }}>{st.metrics.n_fractures}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.textSec }}>{st.metrics.inlier_ratio.toFixed(2)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.textSec }}>{st.metrics.fit_rms_m ?? '—'}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.textSec }}>{st.sra_1e4m2.toFixed(1)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: TH.accent }}>{st.sra_alpha.sra_alpha_1e4m2.toFixed(1)}</td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: st.srv.depth_reliable ? TH.textSec : TH.textMuted }}
                          title={st.srv.reason ?? `α=${st.srv.alpha_m}m · ${st.srv.n_tetrahedra} 四面体`}>
                        {st.srv.srv_1e4m3.toFixed(1)}{!st.srv.depth_reliable && '*'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding: 12, fontSize: 12, color: TH.textMuted, lineHeight: 1.7 }}>
              点击行高亮该段。红线 = 反演缝面走向(俯视);彩点 = 微地震事件(按段着色);
              半透明色块 = Alpha Shape 缝网轮廓(凹包), 其面积即表中的 SRA 凹包。
              SRA 凸包是上界(会把缝间无事件的空白算进去), 凹包由 Alpha Shape 给出、贴合事件分布。
              SRV 带 * 表示该段事件深度疑为投影伪深度, 三维体积仅供参考。
              指标含义:内点率 = 归入缝面的事件占比;RMS = 事件到缝面的拟合误差;
              SRA = 该段事件凸包改造面积。缝高与 SRV 由「压裂模拟」P3D 正演联合给出(多模态融合)。
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
